import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import { ACCOUNT_DISABLED_MESSAGE, endSession, getSessionId, isSessionHandshake, SESSION_IDLE_TIMEOUT_CODE, SESSION_TERMINATED_CODE } from '../../utils/session'
import { beginOperation, endIdleSession, endOperation, getIdleMs } from '../../utils/idle-session'

export const apiClient = axios.create({
	baseURL: process.env.NEXT_PUBLIC_BASE_URL
})

const IDLE_MS_HEADER = 'x-idle-ms'

/**
 * Uploads and file exports are only ever started by the user, and can run for longer than the idle
 * timeout. While one is in flight the user is waiting on it, not away.
 */
const isUserOperation = config => config.data instanceof FormData || config.responseType === 'blob'

const finishOperation = config => {
	if (config?.operationId) endOperation(config.operationId)
}

const getCurrentUser = () => {
	return new Promise((resolve) => {
		if (firebase.auth().currentUser) {
			resolve(firebase.auth().currentUser)
		} else {
			const unsubscribe = firebase.auth().onAuthStateChanged(user => {
				unsubscribe()
				if (user) {
					resolve(user)
				} else {
					resolve(null)
				}
			})
		}
	})
}

apiClient.interceptors.request.use(
	async config => {
		if (isUserOperation(config)) {
			config.operationId = beginOperation()
		}
		const currentUser = await getCurrentUser()
		if (currentUser) {
			const token = await currentUser.getIdToken()
			config.headers.authorization = token
		}
		// Identifies which session this client believes it holds, so the server can tell an
		// existing session asking again from a genuinely new device.
		const sessionId = getSessionId()
		if (sessionId) {
			config.headers['x-session-id'] = sessionId
		}
		// Every request, background ones included, says how long the user has been idle. That is
		// what stops polling and refetches from holding an idle session open: they carry the time
		// of the user's last real interaction, not the time they were sent. Only this API reads it,
		// so it is not offered to other hosts that would have to allow it through CORS.
		const idleMs = getIdleMs()
		if (idleMs !== null && config.baseURL === apiClient.defaults.baseURL) {
			config.headers[IDLE_MS_HEADER] = idleMs
		}
		return config
	})

/**
 * Ends the session and hands back a promise that never settles: the page is being torn down and
 * redirected, so a rejection here has no handler left and surfaces as an unhandled runtime error.
 */
const abandonRequest = reason => {
	endSession(reason)
	return new Promise(() => {})
}

/**
 * Why the session ended, for a rejection that did not say so itself. Displacing a session revokes
 * the displaced device's Firebase tokens, so a forced refresh distinguishes an account that was
 * disabled from one that was simply signed in elsewhere; a token that still refreshes says only
 * that the server stopped honouring this device's session, which is displacement all the same.
 */
const getSessionEndReason = async () => {
	const currentUser = firebase.auth().currentUser
	if (!currentUser) return 'session-terminated'
	try {
		await currentUser.getIdToken(true)
		return 'session-terminated'
	} catch (e) {
		return e.code === 'auth/user-disabled' ? 'account-disabled' : 'session-terminated'
	}
}

apiClient.interceptors.response.use(
	response => {
		finishOperation(response.config)
		return response
	},
	async error => {
		finishOperation(error.config)
		const errorResponse = error.response
		// The account was disabled while the user was signed in, so the server is the first thing
		// to notice. End the session and send them back to sign-in carrying the reason, rather
		// than letting the rejection surface as an unhandled error on a dead page.
		if (errorResponse?.status === 403 && errorResponse?.data?.message === ACCOUNT_DISABLED_MESSAGE) {
			return abandonRequest('account-disabled')
		}
		if (errorResponse?.status === 401) {
			// The server found the session idle before this device's own timer did. Every tab goes,
			// not just this one.
			if (errorResponse?.data?.code === SESSION_IDLE_TIMEOUT_CODE) {
				endIdleSession()
				return new Promise(() => {})
			}
			// The session guard names itself whenever the request got far enough to reach it.
			if (errorResponse?.data?.code === SESSION_TERMINATED_CODE) {
				return abandonRequest('session-terminated')
			}
			// Otherwise the server refused credentials this device believed were good. For a
			// device holding a session that means the session is no longer its own — the server
			// answers a superseded session id with a plain 401 rather than naming the reason —
			// and the only alternative reading, a device still settling its own session, is
			// excluded by the handshake.
			if (getSessionId() && !isSessionHandshake()) {
				return abandonRequest(await getSessionEndReason())
			}
		}
		if (errorResponse && errorResponse.data) {
			if (errorResponse.data.message) {
				const message = Array.isArray(errorResponse.data.message)
					? errorResponse.data.message.join('\r\n')
					: errorResponse.data.message
				const apiError = new Error(message)
				// Carry the machine readable parts through so callers can branch on them; the
				// session conflict dialog needs both.
				if (errorResponse.data.code) apiError.code = errorResponse.data.code
				if (errorResponse.data.existingSession) apiError.existingSession = errorResponse.data.existingSession
				return Promise.reject(apiError)
			} else {
				return Promise.reject(error)
			}
		} else {
			return Promise.reject(error)
		}
	}
)

const { get, post, put, delete: destroy, patch } = apiClient
export { get, post, put, destroy, patch }
